/**
 * MI Auto PIP change detection for policy endorsement approvals.
 *
 * Determines whether a Michigan auto policy endorsement involves PIP-related changes
 * that require agent approval. Three categories of changes can trigger approval:
 *   1. Proof-of-eligibility documents needed (PIP option 4/5/6 + driver/household changes)
 *   2. Household residents changed (drivers added/removed, household members changed)
 *   3. New PIP form required (PIP limit changed, excluded residents changed on option 4)
 *
 * Extracted from staff-fe getChangesForApproval (helpers.js) and staff-appsync-be
 * checkMiAutoPipChanges (approval-rules.ts) to keep both sides in sync.
 *
 * See: https://github.com/BranchInsurance/branch/pull/XXXXX
 */

export type MiAutoPipChangesResult = {
  needsProofOfEligibilityDocuments: boolean;
  householdResidentsChanged: boolean;
  needsNewPIPForm: boolean;
  hasChangesThatNeedApproval: boolean;
};

const PIP_OPTIONS_MAP: Record<string, number> = {
  UNLIMITED: 1,
  '500000': 2,
  '250000': 3,
  '250000W/EXCLUDED': 4,
  '50000': 5,
  OPTOUT: 6
};

export function getPipOptionIndex(pipOption: string, excludedResidentsNum: number): number | undefined {
  if (excludedResidentsNum && pipOption === '250000') {
    return 4;
  }
  return PIP_OPTIONS_MAP[pipOption];
}

export function getMiAutoPipChanges({
  addedChanges,
  removedChanges,
  policyDetails,
  canApproveUWReview,
  isAgency
}: {
  addedChanges: Record<string, any>;
  removedChanges: Record<string, any>;
  policyDetails: Record<string, any>;
  canApproveUWReview: boolean;
  isAgency: boolean;
}): MiAutoPipChangesResult {
  const initialPipOption = policyDetails?.autoCoverage?.policyLimitPIPME;
  const currentPipOption = addedChanges['autoCoverage.policyLimitPIPME'];
  const currentExcludedResidentsNum = addedChanges?.['auto.pipExcludedResidents'] || 0;
  const currentPipOptionIndex = getPipOptionIndex(currentPipOption, currentExcludedResidentsNum);
  const initialExcludedResidents = (policyDetails.drivers || [])
    .concat(policyDetails.nonDrivers || [])
    .some((d: any) => d.pipHasQHC || d.pipOptOut);

  // We used to not have option 4, and as far as rating goes, options 3 and 4 are the same,
  // so identify when we are on option 3 with excluded so we don't show docs when we silently move them to 4.
  // If we have currentExcludedResidentsNum, the number of excluded residents has changed and we need a new form.
  const goingFrom3To4AsPartOfTransition =
    initialPipOption === '250000' &&
    currentPipOptionIndex === 4 &&
    initialExcludedResidents &&
    currentExcludedResidentsNum === 0;

  const addedDriverIds = Object.entries(addedChanges)
    .filter(([key]) => key.includes('drivers.') && key.includes('.id'))
    .map(([, value]) => value);

  const removedDriverIds = Object.entries(removedChanges)
    .filter(([key]) => key.includes('drivers.') && key.includes('.id'))
    .map(([, value]) => value);

  const driverAdded = addedDriverIds.some((id) => !removedDriverIds.includes(id));
  const driverRemoved = removedDriverIds.some((id) => !addedDriverIds.includes(id));

  const maritalStatusOfADriverChanged = Object.keys(removedChanges).some(
    (key) => key.includes('drivers.') && key.includes('maritalStatus') && addedChanges[key] !== removedChanges[key]
  );

  const primaryPersonChanged = Object.keys(addedChanges)
    .filter((key) => key.includes('drivers.') && key.includes('isPrimary'))
    .some((key) => addedChanges[key] === true && removedChanges[key] === false);

  const addedExcludedResident =
    addedChanges.hasOwnProperty('auto.pipExcludedResidents') &&
    removedChanges.hasOwnProperty('auto.pipExcludedResidents') &&
    currentExcludedResidentsNum > removedChanges['auto.pipExcludedResidents'];

  const removedExcludedResident =
    removedChanges.hasOwnProperty('auto.pipExcludedResidents') &&
    addedChanges.hasOwnProperty('auto.pipExcludedResidents') &&
    removedChanges['auto.pipExcludedResidents'] > currentExcludedResidentsNum;

  const pipExcludedResidentsChanged = addedExcludedResident || removedExcludedResident;

  const pipLimitChanged =
    addedChanges.hasOwnProperty('autoCoverage.policyLimitPIPME') &&
    removedChanges.hasOwnProperty('autoCoverage.policyLimitPIPME') &&
    currentPipOption !== removedChanges['autoCoverage.policyLimitPIPME'] &&
    // except when transitioning from old option 3 to new option 4
    !goingFrom3To4AsPartOfTransition;

  const addedHouseholdMember =
    addedChanges.hasOwnProperty('auto.pipAllResidents') &&
    removedChanges.hasOwnProperty('auto.pipAllResidents') &&
    addedChanges['auto.pipAllResidents'] > removedChanges['auto.pipAllResidents'];

  const removedHouseholdMember =
    removedChanges.hasOwnProperty('auto.pipAllResidents') &&
    addedChanges.hasOwnProperty('auto.pipAllResidents') &&
    removedChanges['auto.pipAllResidents'] > addedChanges['auto.pipAllResidents'];

  // Proof-of-eligibility documents conditions
  const conditionForProofOfElegibilityDocuments1 =
    [5, 6].includes(currentPipOptionIndex!) &&
    (driverAdded || primaryPersonChanged || maritalStatusOfADriverChanged || addedHouseholdMember);

  const conditionForProofOfElegibilityDocuments2 =
    currentPipOptionIndex === 4 &&
    (primaryPersonChanged || maritalStatusOfADriverChanged || (pipExcludedResidentsChanged && addedExcludedResident));

  const conditionForProofOfElegibilityDocuments3 =
    currentPipOption &&
    !goingFrom3To4AsPartOfTransition &&
    [4, 5, 6].includes(
      getPipOptionIndex(currentPipOption, currentExcludedResidentsNum || currentExcludedResidentsNum)!
    );

  const needsProofOfEligibilityDocuments =
    !!(
      conditionForProofOfElegibilityDocuments1 ||
      conditionForProofOfElegibilityDocuments2 ||
      conditionForProofOfElegibilityDocuments3
    ) &&
    (!canApproveUWReview || isAgency);

  // All household residents included conditions
  const householdResidentsChanged = !!(driverAdded || driverRemoved || removedHouseholdMember || addedHouseholdMember);

  // New PIP form required conditions
  const needsNewPIPForm = !!(pipLimitChanged || (pipExcludedResidentsChanged && currentPipOptionIndex === 4));

  return {
    needsProofOfEligibilityDocuments,
    householdResidentsChanged,
    needsNewPIPForm,
    hasChangesThatNeedApproval: needsProofOfEligibilityDocuments || householdResidentsChanged || needsNewPIPForm
  };
}
